import { SHOPIFY_STORE_DOMAIN } from '@portal/config/base';
import { StoreCode, storeCodeSchema } from 'schema/storeCode';
import { AddToCartSectionShop, BuildBundleV3, SimplifiedBaB } from 'types/studio/studio';
import { HIDDEN_PRODUCT_TAG, SHOPIFY_GRAPHQL_API_ENDPOINT, ShopifyFetchError } from './constants';
import { isShopifyError } from './type-guards';
import {
  CartLine,
  CollectionProduct,
  Connection,
  Image,
  Product,
  ShopifyProduct,
  ShopifyResultProductsMap,
  UnwrapConnection
} from './types';

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
  stringToCheck.startsWith(startsWith) ? stringToCheck : `${startsWith}${stringToCheck}`;

export const getShopifyProductsMap = (
  products: CollectionProduct[] | Product[],
  productsVariantIDs: Set<string>
) => {
  const result: ShopifyResultProductsMap = {};

  products.forEach((product) => {
    product.variants.forEach((variant) => {
      if (productsVariantIDs.has(`${variant.id}`)) {
        result[variant.id] = { ...product, currentVariant: variant };
      }
    });
  });

  return result;
};

type MetafieldsShopifyItem = {
  key: string;
  namespace: string;
  value: string;
  __parentId: string;
  __typename: string;
};

export function normalizeCartMetafields(metafieldsBase: MetafieldsShopifyItem[]) {
  if (!Array.isArray(metafieldsBase)) return metafieldsBase;

  const metafields: {
    [key: string]: string;
  } = {};

  metafieldsBase.forEach((metafield) => {
    if (!metafield) {
      return;
    }

    const { key, value } = metafield;

    if (!metafields[key]) {
      metafields[key] = '';
    }

    metafields[key] = value;
  });

  return metafields;
}

type ExtractVariables<T> = T extends { variables: object } ? T['variables'] : never;

export async function shopifyFetch<T>({
  cache = 'force-cache',
  headers,
  query,
  tags,
  shopifyDomain,
  accessToken,
  variables,
  endpoint
}: {
  cache?: RequestCache;
  headers?: HeadersInit;
  query: string;
  tags?: string[];
  shopifyDomain: string;
  accessToken: string;
  variables?: ExtractVariables<T>;
  endpoint?: string;
}): Promise<{ status: number; body: T } | never> {
  const domain = SHOPIFY_STORE_DOMAIN ? ensureStartsWith(shopifyDomain, 'https://') : '';
  const endpointToUse = endpoint || `${domain}${SHOPIFY_GRAPHQL_API_ENDPOINT}`;
  const key = accessToken!;

  try {
    const result = await fetch(endpointToUse, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': key,
        ...headers
      },
      body: JSON.stringify({
        ...(query && { query }),
        ...(variables && { variables })
      }),
      cache,
      ...(tags && { next: { tags } })
    });

    const body = await result.json();

    if (body.errors) {
      throw body.errors[0];
    }

    return {
      status: result.status,
      body
    };
  } catch (e: unknown) {
    if (e instanceof Error) {
      if (isShopifyError(e)) {
        throw new ShopifyFetchError(e, query, e.status || 500);
      }
      throw new ShopifyFetchError(e, query);
    } else {
      throw new ShopifyFetchError(new Error('An unknown error occurred'), query);
    }
  }
}

export const removeEdgesAndNodes = (array: Connection<any>) => {
  if (array.edges) {
    return array.edges.map((edge) => edge?.node);
  }
  if (array.nodes) {
    return array.nodes;
  }
  return [];
};

export function recursivelyRemoveEdgesAndNodes<T extends Record<string, unknown>>(
  data: T
): UnwrapConnection<T> {
  const entries = Object.entries(data);

  const { arrays, objects } = entries.reduce<{
    arrays: unknown[];
    objects: Record<string, unknown>;
  }>(
    (acc, [key, value]) => {
      if (Array.isArray(value) && (key === 'edges' || key === 'nodes')) {
        return {
          ...acc,
          arrays: [
            ...acc.arrays,
            ...value.map((item) =>
              recursivelyRemoveEdgesAndNodes(key === 'edges' ? item.node : item)
            )
          ]
        };
      }

      return {
        ...acc,
        objects: {
          ...acc.objects,
          [key]:
            typeof value === 'object' && value !== null && Object.keys(value).length > 0
              ? // @ts-ignore - Recursive type handling for deeply nested GraphQL objects
                recursivelyRemoveEdgesAndNodes(value)
              : value
        }
      };
    },
    { arrays: [], objects: {} }
  );

  return (arrays.length > 0 ? arrays : objects) as UnwrapConnection<T>;
}

const reshapeImages = (images: Connection<Image>, productTitle: string) => {
  const flattened = removeEdgesAndNodes(images);

  return flattened.map((image) => {
    let filename = '';

    if (image.url) {
      [, filename] = image.url.match(/.*\/(.*)\..*/)[1] || [];
    }

    if (image.src) {
      [, filename] = image.src.match(/.*\/(.*)\..*/) || [];
    }

    return {
      ...image,
      altText: image.altText || `${productTitle} - ${filename}`
    };
  });
};

const reshapeProduct = (product: ShopifyProduct, filterHiddenProducts: boolean = true) => {
  if (!product || (filterHiddenProducts && product.tags.includes(HIDDEN_PRODUCT_TAG))) {
    return undefined;
  }

  const { images, variants, ...rest } = product;

  return {
    ...rest,
    images: reshapeImages(images, product.title),
    variants: removeEdgesAndNodes(variants)
  };
};

export const reshapeProducts = (products: ShopifyProduct[]): Product[] =>
  products
    .filter((product) => !!product)
    .map((product) => reshapeProduct(product))
    .filter((reshapedProduct): reshapedProduct is Product => !!reshapedProduct);

type BabV3GiftsType = { variantId: string | undefined; targetStore: StoreCode; amount?: number }[];

const getBabV3GiftProducts = (storeCode: StoreCode, gifts?: BabV3GiftsType) => {
  if (!Array.isArray(gifts)) return [];

  const giftProductIds = gifts.filter(({ targetStore }) => targetStore === storeCode);

  return giftProductIds.length ? giftProductIds.map((gift) => gift.variantId).filter(Boolean) : [];
};

export const getShopifyProductVariantIDs = (
  firstVariant: BuildBundleV3['firstVariant'],
  secondVariant: BuildBundleV3['secondVariant'],
  storeCode: StoreCode
) => {
  const targetStoreFirstVariantSingle = firstVariant.singleSubscription.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreFirstVariantDouble = firstVariant.doubleSubscription.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreFirstVariantFamily = firstVariant.familySubscription?.ProductIds?.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreFirstVariantOneTime = firstVariant.oneTime.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreSecondVariantSingle = secondVariant.singleSubscription.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreSecondVariantDouble = secondVariant.doubleSubscription.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreSecondVariantFamily = firstVariant.familySubscription?.ProductIds?.find(
    ({ targetStore }) => targetStore === storeCode
  );
  const targetStoreSecondVariantOneTime = secondVariant.oneTime.ProductIds.find(
    ({ targetStore }) => targetStore === storeCode
  );

  const newIdsArray = [
    ...getBabV3GiftProducts(
      storeCode,
      (firstVariant.singleSubscription.giftProducts ||
        firstVariant.singleSubscription.gifts) as BabV3GiftsType
    ),
    ...getBabV3GiftProducts(
      storeCode,
      (firstVariant.doubleSubscription.giftProducts ||
        firstVariant.doubleSubscription.gifts) as BabV3GiftsType
    ),
    ...getBabV3GiftProducts(
      storeCode,
      (firstVariant.familySubscription?.giftProducts ||
        firstVariant.familySubscription?.gifts) as BabV3GiftsType
    ),
    ...getBabV3GiftProducts(
      storeCode,
      (secondVariant.singleSubscription.giftProducts ||
        secondVariant.singleSubscription.gifts) as BabV3GiftsType
    ),
    ...getBabV3GiftProducts(
      storeCode,
      (secondVariant.doubleSubscription.giftProducts ||
        secondVariant.doubleSubscription.gifts) as BabV3GiftsType
    ),
    ...getBabV3GiftProducts(
      storeCode,
      (secondVariant.familySubscription?.giftProducts ||
        secondVariant.familySubscription?.gifts) as BabV3GiftsType
    )
  ];

  if (targetStoreFirstVariantSingle)
    newIdsArray.push(targetStoreFirstVariantSingle.productVariantId);
  if (targetStoreFirstVariantDouble)
    newIdsArray.push(targetStoreFirstVariantDouble.productVariantId);
  if (targetStoreFirstVariantFamily)
    newIdsArray.push(targetStoreFirstVariantFamily.productVariantId);
  if (targetStoreFirstVariantOneTime)
    newIdsArray.push(targetStoreFirstVariantOneTime.productVariantId);
  if (targetStoreSecondVariantSingle)
    newIdsArray.push(targetStoreSecondVariantSingle.productVariantId);
  if (targetStoreSecondVariantDouble)
    newIdsArray.push(targetStoreSecondVariantDouble.productVariantId);
  if (targetStoreSecondVariantFamily)
    newIdsArray.push(targetStoreSecondVariantFamily.productVariantId);
  if (targetStoreSecondVariantOneTime)
    newIdsArray.push(targetStoreSecondVariantOneTime.productVariantId);

  const idsArray = newIdsArray.map((variant: string) => `gid://shopify/ProductVariant/${variant}`);

  const result = new Set(idsArray);

  return result;
};

export const getShopifyProductVariantIDsForCart = (
  variants: AddToCartSectionShop['variants'],
  storeCode: StoreCode
) => {
  const idsArray = variants.reduce(
    (acc, variant) => ({
      productVariantIds: [
        ...acc.productVariantIds,
        variant?.oneTime.productIds.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.singleSubscription.productIds.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.doubleSubscription.productIds.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.familySubscription?.productIds?.find(
          ({ targetStore }) => targetStore === storeCode
        )?.productVariantId
      ],
      giftProducts: [
        ...acc.giftProducts,
        ...getBabV3GiftProducts(storeCode, variant.oneTime.giftProducts as BabV3GiftsType),
        ...getBabV3GiftProducts(
          storeCode,
          variant.singleSubscription.giftProducts as BabV3GiftsType
        ),
        ...getBabV3GiftProducts(
          storeCode,
          variant.doubleSubscription.giftProducts as BabV3GiftsType
        ),
        ...getBabV3GiftProducts(
          storeCode,
          variant.familySubscription?.giftProducts as BabV3GiftsType
        )
        // ...(variant?.oneTime.giftProducts ?? []),
        // ...(variant?.singleSubscription.giftProducts ?? []),
        // ...(variant?.doubleSubscription.giftProducts ?? [])
      ]
    }),
    {
      productVariantIds: [],
      giftProducts: []
    }
  );

  const result = [...idsArray.productVariantIds, ...idsArray.giftProducts].map(
    (variant: string) => `gid://shopify/ProductVariant/${variant}`
  );

  return new Set(result);
};

export const getShopifyProductVariantIDsForSimplifiedBaB = (
  variants: SimplifiedBaB['variants'],
  storeCode: StoreCode
) => {
  const idsArray = variants.reduce(
    (acc, variant) => ({
      productVariantIds: [
        ...acc.productVariantIds,
        variant?.oneTime.productIds?.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.singleSubscription.productIds?.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.doubleSubscription.productIds?.find(({ targetStore }) => targetStore === storeCode)
          ?.productVariantId,
        variant?.familySubscription?.productIds?.find(
          ({ targetStore }) => targetStore === storeCode
        )?.productVariantId
      ],
      giftProducts: [
        ...acc.giftProducts,
        ...getBabV3GiftProducts(storeCode, variant.oneTime.giftProducts as BabV3GiftsType),
        ...getBabV3GiftProducts(
          storeCode,
          variant.singleSubscription.giftProducts as BabV3GiftsType
        ),
        ...getBabV3GiftProducts(
          storeCode,
          variant.doubleSubscription.giftProducts as BabV3GiftsType
        ),
        ...getBabV3GiftProducts(
          storeCode,
          variant.familySubscription?.giftProducts as BabV3GiftsType
        )
        // ...(variant?.oneTime.giftProducts ?? []),
        // ...(variant?.singleSubscription.giftProducts ?? []),
        // ...(variant?.doubleSubscription.giftProducts ?? [])
      ]
    }),
    {
      productVariantIds: [],
      giftProducts: []
    }
  );

  const result = [...idsArray.productVariantIds, ...idsArray.giftProducts].map(
    (variant: string) => `gid://shopify/ProductVariant/${variant}`
  );

  return new Set(result);
};

// if true, it means the current store can use shopify collection logic
// because it's set up properly on sanity and shopify
// currently EU and UK store are not ready
// update this as needed
export const hasUsableBaBCollection = (currentStore: StoreCode) => {
  return currentStore !== storeCodeSchema.enum.eu;
};

export function formatCartLines(cartData: any): CartLine[] {
  const edges = cartData?.lines?.edges;
  if (!Array.isArray(edges)) return [];
  return edges.map((edge: any) => {
    const line: CartLine = {
      id: edge.node.id,
      quantity: edge.node.quantity,
      attributes: edge.node.attributes?.map((attribute: any) => ({
        key: attribute.key,
        value: attribute.value
      })),
      cost: {
        totalAmount: edge.node.cost.totalAmount,
        subtotalAmount: edge.node.cost.subtotalAmount,
        amountPerQuantity: edge.node.cost.amountPerQuantity,
        compareAtAmountPerQuantity: edge.node.cost.compareAtAmountPerQuantity
      },
      merchandise: {
        id: edge.node.merchandise.id,
        ...edge.node.merchandise,
        title: edge.node.merchandise.title,
        metafields: edge.node.merchandise.metafields
          ? normalizeCartMetafields(edge.node.merchandise.metafields)
          : null,
        priceV2: edge.node.merchandise.priceV2,
        price: edge.node.merchandise.price,
        product: {
          title: edge.node.merchandise.product.title,
          images: edge.node.merchandise.product.images.edges.map((image: any) => ({
            url: image.node.url,
            altText: image.node.altText,
            width: image.node.width,
            height: image.node.height
          })),
          metafields: edge.node.merchandise.product.metafields
            ? normalizeCartMetafields(edge.node.merchandise.product.metafields)
            : null
        }
      },
      discountAllocations: edge.node.discountAllocations?.map((discountAllocation: any) => ({
        discountedAmount: {
          amount: discountAllocation.discountedAmount.amount,
          currencyCode: discountAllocation.discountedAmount.currencyCode
        }
      })),
      sellingPlanAllocation: edge.node.sellingPlanAllocation
    };

    return line;
  });
}
